import { HTTP } from './http-constants'
import { AxiosPromise } from 'axios'
import { MessageRequest, MessageResponse } from '@/types/message'
import { ModuleSuggestionRequest } from '@/types/module-suggestion'
import { UserRequest } from '@/types/user'
import { NewsletterRequest } from '@/types/newsletter'

export class ApiService {
    /** Operations **/
    public getAllOpe() {
        return HTTP.get('/operations')
    }
    public getOperation(id) {
        return HTTP.get(`/operations/${id}`)
    }
    public getOperationDetails(id: number): AxiosPromise<any> {
        return HTTP.get(`/operations/details/${id}`)
    }

    /** Users **/
    public login(username, password) {
        return HTTP.post('/login-check', {
            username: username,
            password: password,
        })
    }
    public postUserSignup(user, ope) {
        if (ope !== 0) {
            return HTTP.post('/users/' + ope, user)
        } else {
            return HTTP.post('/users', user)
        }
    }
    public postUser(user: UserRequest) {
        return HTTP.post(`/users`, user)
    }
    public postUserNewsletter(req: NewsletterRequest) {
        return HTTP.post(`/users/newsletter`, req)
    }
    public patchUserSignup(id, user) {
        return HTTP.patch(`/buyer/users/${id}`, user)
    }
    public postResetPasswordRequest(mailObject) {
        return HTTP.post(`/reset_password/request`, mailObject)
    }
    public postResetPasswordChange(token, passwordObject) {
        return HTTP.post(`/reset_password/change/${token}`, passwordObject)
    }
    public getEmailSignUp(email) {
        return HTTP.head(`/users/${email}`)
    }
    public getBuyer(id) {
        return HTTP.get(`/buyer/users/${id}`)
    }
    public patchBuyer(id, user) {
        return HTTP.patch(`/buyer/users/${id}`, user)
    }
    public patchUser(id, user) {
        return HTTP.patch(`/users/${id}`, user)
    }
    public getBuyerOperations(id) {
        return HTTP.get(`/buyer/users/${id}/operations`)
    }
    public getUserOperations(id) {
        return HTTP.get(`/buyer/users/${id}/user_operations`)
    }
    public getBuyerProjects(id, opeId) {
        return HTTP.get(`/buyer/projects/${id}/${opeId}`)
    }
    public patchBuyerFoyer(id, foyer) {
        return HTTP.patch(`/buyer/foyer/${id}`, foyer)
    }
    public async getUserOperation(userId, operationId) {
        const resp = await HTTP.get(`/buyer/users/${userId}/user_operations`)
        const userOpe = resp.data.find((el: any) => el.operation && el.operation.id === operationId)
        if (!userOpe) throw { response: { status: 404, data: 'UserOperation not found' } }
        return { data: userOpe }
    }
    public patchUserOperation(userId, operationId, userOperation) {
        return HTTP.patch(`/buyer/users/${userId}/operations/${operationId}`, userOperation)
    }
    public postRgpd(userId: number, req: Object): AxiosPromise {
        return HTTP.post(`/buyer/rgpd/delete/${userId}`, req)
    }
    public patchPreferences(userID: number, req: Object): AxiosPromise {
        return HTTP.patch(`/buyer/users/${userID}/preferences`, req)
    }
    public getUserNotifications(userID: number): AxiosPromise {
        return HTTP.get(`/buyer/users/${userID}/notifications`)
    }
    public patchAttachUserToOperation(userID: number, opeID: number) {
        return HTTP.patch(`/buyer/users/${userID}/attach_to_operation/${opeID}`)
    }
    public patchDetachUserFromOperation(userID: number, opeID: number) {
        return HTTP.patch(`/buyer/users/${userID}/detach_from_operation/${opeID}`)
    }
    public patchSaveFavorite(userID: number, opeID: number, req) {
        return HTTP.patch(`/buyer/users/${userID}/save_favorite/${opeID}`, req)
    }

    public getOperationUsers(operationId: number, filter: string) {
        const filterPath = filter ? `/${filter}` : ''
        return HTTP.get(`/buyer/users/operation/${operationId}${filterPath}`)
    }

    /**
     * Notifications
     */
    public patchUserNotification(
        userID: number,
        notificationID: number,
        req: Object
    ): AxiosPromise {
        return HTTP.patch(
            `/buyer/users/${userID}/notifications/${notificationID}`,
            req
        )
    }

    /**
     * Groups
     */
    public getAllGroups() {
        return HTTP.get(`/buyer/groups`)
    }
    public getGroupsByOperation(operationId) {
        return HTTP.get(`/buyer/groups/operation/${operationId}`)
    }
    public getGroup(id) {
        return HTTP.get(`/buyer/groups/${id}`)
    }
    public postGroup(group) {
        return HTTP.post('/buyer/groups', group)
    }
    public postJoinGroup(groupID, req) {
        return HTTP.post(`/buyer/groups/${groupID}/users`, req)
    }
    public patchGroup(groupID, req) {
        return HTTP.patch(`/buyer/groups/${groupID}`, req)
    }
    public deleteUserFromGroup(groupID, userID) {
        return HTTP.delete(`/buyer/groups/${groupID}/users/${userID}`)
    }
    public getGroupAlbums(groupID) {
        return HTTP.get(`/buyer/groups/${groupID}/albums`)
    }
    public postGroupImage(groupID, userID, req) {
        return HTTP.post(`/buyer/groups/${groupID}/users/${userID}/albums`, req)
    }
    public deleteGroupImage(imageID) {
        return HTTP.delete(`/buyer/groups/albums/${imageID}`)
    }
    public inviteUserInGroup(groupId, userId) {
        return HTTP.get(`/buyer/groups/${groupId}/invite/${userId}`)
    }

    /** Reponses */
    public postResponse(req) {
        return HTTP.post(`/buyer/responses`, req)
    }
    public patchResponse(responseID: number, req) {
        return HTTP.patch(`/buyer/responses/${responseID}`, req)
    }
    public getResponse(questionID: number) {
        return HTTP.get(`/buyer/responses/${questionID}`)
    }

    /** Modules */
    public getModules() {
        return HTTP.get(`/buyer/modules`)
    }
    public getModule(moduleID) {
        return HTTP.get(`/buyer/modules/${moduleID}`)
    }
    public getQuestions(moduleID) {
        return HTTP.get(`/buyer/modules/${moduleID}/questions`)
    }
    public postModuleSuggestion(req: ModuleSuggestionRequest) {
        return HTTP.post(`/buyer/module-suggestions`, req)
    }

    /** Questions */
    public getQuestion(questionID) {
        return HTTP.get(`/buyer/questions/${questionID}`)
    }
    public getPropositions(questionID) {
        return HTTP.get(`/buyer/questions/${questionID}/propositions`)
    }

    /** Messages */
    public postMessage(req: MessageRequest) {
        return HTTP.post(`/buyer/messages`, req)
    }
    public getMessages(threadID: number) {
        return HTTP.get<MessageResponse[]>(
            `/buyer/threads/${threadID}/messages`
        )
    }

    /** Parameters */
    public getParameters() {
        return HTTP.get(`/buyer/parameters/surfaces`)
    }

    /** Hobbies **/
    public getHobbies() {
        return HTTP.get('/hobbies')
    }
    // public getHobbyTypes() {
    //     return HTTP.get('/hobby-types');
    // }
    public postHobbySuggest(hobby) {
        return HTTP.post('/buyer/hobby-suggestions', hobby)
    }

    /** Categories **/
    public getCategories() {
        return HTTP.get('/categories')
    }

    /** Avatars **/
    public getAvatars() {
        return HTTP.get('/users/avatars')
    }

    /** IdentiKits **/
    public getIdentiKits() {
        return HTTP.get('/identikits')
    }
    public postIdentiKitSuggest(identikit) {
        return HTTP.post('/buyer/identikit-suggestions', identikit)
    }

    /** Shared Spaces **/
    public getSharedSpaces() {
        return HTTP.get('/shared-spaces')
    }
    public postSpaceSuggest(space) {
        return HTTP.post('/buyer/shared-space-suggestions', space)
    }

    /** Areas **/
    public getAreas() {
        return HTTP.get('/areas')
    }

    /** Rooms **/
    public getRooms() {
        return HTTP.get('/rooms')
    }

    /** Lock **/
    public postFunding(id, req) {
        return HTTP.post(`/buyer/users/${id}/funding`, req)
    }
    public postIdentity(id, req) {
        return HTTP.post(`/buyer/users/${id}/identity`, req)
    }
    public postPurchaseLetter(id, req) {
        return HTTP.post(`/buyer/users/${id}/purchase-letter`, req)
    }
    public postBrs(id, req) {
        return HTTP.post(`/buyer/users/${id}/brs`, req)
    }
    public postOperationPurchaseLetterModel(opeId, req) {
        return HTTP.post(`/buyer/operations/${opeId}/purchase-letter-model`, req)
    }
    public postOperationBrsAttestationModel(opeId, req) {
        return HTTP.post(`/buyer/operations/${opeId}/brs-attestation-model`, req)
    }

    /** Alertes */
    public getAlerts(operationId) {
        return HTTP.get(`/buyer/alerts/${operationId}`)
    }

    /** Plans **/
    public getOpeBluePrints(opeId) {
        return HTTP.get(`/buyer/operations/${opeId}/blueprints`)
    }

    public postOpeBluePrint(opeId, req) {
        return HTTP.post(`/buyer/operations/${opeId}/blueprints`, req)
    }

    /** Admin Notifications **/
    public postAdminNotification(req: { type: string; message: string; context?: object }) {
        return HTTP.post(`/buyer/admin-notifications`, req)
    }

    /**
     * Step advancement
     * Étape 1 : le client demande un plan à l'architecte.
     * Le backend envoie une notification à l'architecte.
     */
    public postRequestPlan(userId: number, opeId: number) {
        return HTTP.post(`/buyer/users/${userId}/request-plan/${opeId}`)
    }

    /**
     * Étape 1 → 2 : le client valide le plan générique reçu.
     * Le backend fait avancer l'utilisateur à l'étape suivante.
     */
    public postValidatePlan(userId: number, opeId: number) {
        return HTTP.post(`/buyer/users/${userId}/validate-plan/${opeId}`)
    }

    /**
     * Étape 3 → 4 : le client valide la proposition de prix.
     * Le backend enregistre la validation et fait avancer à l'étape 4.
     */
    public postValidatePrice(userId: number, opeId: number) {
        return HTTP.post(`/buyer/users/${userId}/validate-price/${opeId}`)
    }
}
